import {groupBy, mapObject} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {ModalMetadataEntryType} from 'constants/AnalyticsConstants';
import StorableChip from 'inventory/chips/StorableChip';
import ColumnSelector from 'notebook/sample-propagation/add-samples-modal/ColumnSelector';
import {TargetStorableNoIcon} from 'notebook/structured-tables/action-functions/inventoryShortcutsActionFunction';
import {
  isProcessOutputStructuredTableColumn,
  isProcessTaskStructuredTableColumn,
  isTagValueStructuredTableColumn,
} from 'notebook/structured-tables/columns/TypePredicates';
import {
  ColumnWithPositionalIndex,
  StructuredTable,
  StructuredTableColumn,
  StructuredTableKind,
  StructuredTableRow,
  StructuredTableSchema,
  TagValueStructuredTableColumn,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {StorableLinkSchemaFieldTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import MoveStorablesFromStructuredTableModal, {
  ModalTypeProps,
} from 'samples/MoveStorablesFromStructuredTableModal';
import MoveStorablesModal, {NATIVE_INPUT_MODE} from 'samples/MoveStorablesModal';
import {isRequiredLinkStorableSchema, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_DARKER, GRAY_LIGHTER} from 'ui-library/Colors';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import APIUtil from 'util/APIUtil';
import {Optional} from 'util/Types';

interface Props<TTableMetadata, TRowMetadata>
  extends Omit<ModalTypeProps<TTableMetadata, TRowMetadata>, 'storables'> {}

interface ExtractedRowData {
  taskHumanId: Optional<string>;
  destinationStorables: Array<{id: string}>;
  targetStorables: Array<TargetStorableNoIcon>;
}

enum ProcessTableKindLabel {
  TASK = 'task',
  OUTPUT = 'output',
}

enum ValidationErrorType {
  // We require a parent storable even if the storables being moved are themselves (root) locations
  NO_PARENT_STORABLE = 'NO_PARENT_STORABLE',
  // Multiple parents are indicated across all tasks that contain the storable being moved
  MULTIPLE_PARENT_STORABLES = 'MULTIPLE_PARENT_STORABLES',
}

interface ValidationErrorData {
  errorType: ValidationErrorType;
  errorMessage: string;
  invalidTargetStorableIds: Set<string>;
}

type GetValidationError = (
  extractedRowDatas: Array<ExtractedRowData>,
  tableKindLabel: ProcessTableKindLabel
) => ValidationErrorData | null;

function getSchemaFieldForColumn(
  column: TagValueStructuredTableColumn,
  tableSchemas: Array<StructuredTableSchema>
): SchemaField {
  const matchingSchemaField = tableSchemas
    .flatMap((schema) => schema.fields)
    .find(({apiId}) => apiId === column.schemaFieldApiId);
  assert(matchingSchemaField != null, `Expected schema to have schema field ${column.schemaFieldApiId}`);
  return matchingSchemaField;
}

function doesColumnHaveLinkedLocationSchema(
  column: StructuredTableColumn,
  tableSchemas: Array<StructuredTableSchema>
): boolean {
  if (!isTagValueStructuredTableColumn(column)) {
    return false;
  }
  const columnSchemaField = getSchemaFieldForColumn(column, tableSchemas);
  if (!isRequiredLinkStorableSchema(columnSchemaField.requiredLink)) {
    return false;
  }
  const storableSchema = columnSchemaField.requiredLink.storableSchema;
  return storableSchema != null && APIUtil.isLocationSchema(storableSchema.id);
}

function getProcessTaskOrOutputColumnWithIndex(
  columns: Array<StructuredTableColumn>,
  tableKindLabel: ProcessTableKindLabel
): ColumnWithPositionalIndex {
  const processColumnPredicate =
    tableKindLabel === ProcessTableKindLabel.TASK
      ? isProcessTaskStructuredTableColumn
      : isProcessOutputStructuredTableColumn;
  const processColumnsWithIndices = columns
    .map((column, index) => ({column, columnIndex: index}))
    .filter(({column}) => processColumnPredicate(column));
  assert(processColumnsWithIndices.length === 1, `Expected exactly 1 ${tableKindLabel} column`);
  return processColumnsWithIndices[0];
}

// Pulls out the important data we care about from each row so the rest of this file doesn't have to deal
// directly with structure table rows
function getExtractedRowDatas(
  targetRowsAndStorables: Array<{
    targetStorables: Array<TargetStorableNoIcon>;
    structuredTableRow: StructuredTableRow<unknown>;
  }>,
  processTaskColumnWithIndex: ColumnWithPositionalIndex,
  selectedDestinationColumnWithIndex: ColumnWithPositionalIndex
): Array<ExtractedRowData> {
  return targetRowsAndStorables.map(({targetStorables, structuredTableRow}) => {
    const typedValue = structuredTableRow.cells[processTaskColumnWithIndex.columnIndex].typedValue;
    const taskHumanId = typedValue ? formatTypedValue(typedValue) : null;

    const destinationStorablesTypedValue = structuredTableRow.cells[
      selectedDestinationColumnWithIndex.columnIndex
    ].typedValue as StorableLinkSchemaFieldTypedValue | undefined;
    const destinationStorables = destinationStorablesTypedValue?.targetStorables ?? [];

    return {taskHumanId, destinationStorables, targetStorables};
  });
}

function groupExtractedRowDatasByStorableId(extractedRowDatas: Array<ExtractedRowData>): {
  [storableId: string]: Array<ExtractedRowData>;
} {
  const storableIdAndRowDatasByStorableId: {
    [storableId: string]: Array<{storableId: string; rowData: ExtractedRowData}>;
  } = groupBy(
    extractedRowDatas.flatMap((rowData) =>
      rowData.targetStorables.map(({id}) => ({storableId: id, rowData}))
    ),
    ({storableId}) => storableId
  );
  return mapObject(storableIdAndRowDatasByStorableId, (storableIdAndRowDatas) =>
    storableIdAndRowDatas.map(({rowData}) => rowData)
  );
}

const validateParentStorableIsPresent: GetValidationError = (extractedRowDatas, tableKindLabel) => {
  const invalidTaskRowDatas = extractedRowDatas.filter(
    ({destinationStorables}) => destinationStorables.length === 0
  );
  const invalidTargetStorableIds = new Set(
    invalidTaskRowDatas.flatMap(({targetStorables}) => targetStorables).map(({id}) => id)
  );
  if (invalidTargetStorableIds.size === 0) {
    return null;
  }

  const errorMessage = `The following ${pluralize(
    invalidTaskRowDatas.length,
    `${tableKindLabel} doesn't`,
    `${tableKindLabel}s don't`
  )} have a location entered: ${invalidTaskRowDatas.map(({taskHumanId}) => taskHumanId).join(', ')}`;
  return {errorType: ValidationErrorType.NO_PARENT_STORABLE, errorMessage, invalidTargetStorableIds};
};

const validateParentStorableIsSingular: GetValidationError = (extractedRowDatas, tableKindLabel) => {
  const targetStorableById = new Map(
    extractedRowDatas
      .flatMap(({targetStorables}) => targetStorables)
      .map((targetStorable) => [targetStorable.id, targetStorable])
  );
  const extractedRowDatasByStorableId = groupExtractedRowDatasByStorableId(extractedRowDatas);
  const parentIdsByStorableId: {[storableId: string]: Set<string>} = mapObject(
    extractedRowDatasByStorableId,
    (rowDatas) =>
      new Set(rowDatas.flatMap(({destinationStorables}) => destinationStorables).map(({id}) => id))
  );

  const invalidTargetStorableIds: Array<string> = Object.entries(parentIdsByStorableId)
    .filter(([_, parentIds]) => parentIds.size > 1)
    .map(([storableId, _]) => storableId);

  if (invalidTargetStorableIds.length === 0) {
    return null;
  }

  const errorMessage = `The following ${pluralize(
    invalidTargetStorableIds.length,
    'inventory item appears',
    'inventory items appear'
  )} in ${tableKindLabel}(s) which indicate different locations. You can only move an item to a single location at a time: ${invalidTargetStorableIds
    // TUBE001: TSK-T1, TSK-T2; TUBE002: TSK-T1
    .map((invalidId) => {
      const invalidStorableName = targetStorableById.get(invalidId)!.name;
      const associatedTaskHumanIds = extractedRowDatasByStorableId[invalidId].map(
        ({taskHumanId}) => taskHumanId
      );
      return `${invalidStorableName}: ${associatedTaskHumanIds.join(', ')}`;
    })
    .join('; ')}`;
  return {
    errorType: ValidationErrorType.MULTIPLE_PARENT_STORABLES,
    errorMessage,
    invalidTargetStorableIds: new Set(invalidTargetStorableIds),
  };
};

const VALIDATORS_BY_ERROR_TYPE: {
  [errorType in ValidationErrorType]: GetValidationError;
} = {
  [ValidationErrorType.NO_PARENT_STORABLE]: validateParentStorableIsPresent,
  [ValidationErrorType.MULTIPLE_PARENT_STORABLES]: validateParentStorableIsSingular,
};

function getValidationErrorDatas(
  extractedRowDatas: Array<ExtractedRowData>,
  tableKindLabel: ProcessTableKindLabel
): Array<ValidationErrorData> {
  return Object.values(VALIDATORS_BY_ERROR_TYPE)
    .map((validator) => validator(extractedRowDatas, tableKindLabel))
    .filter((errorData): errorData is ValidationErrorData => errorData != null);
}

/** Provides an input for selecting a location column, and the currently selected value.
 *
 * This custom hook abstracts away complexity that arises from the fact that only a subset of the table's
 * columns can be used for the move destination. Eg. tracking different types of indexes for the column
 * selector input, and handling the no-valid-column case
 */
function useSelectableDestinationColumn({
  structuredTable,
  targetStorableColumnWithIndex,
  numTasks,
}: {
  structuredTable: StructuredTable<unknown, unknown>;
  targetStorableColumnWithIndex: ColumnWithPositionalIndex;
  numTasks: number;
}): {
  destinationColumnSelectorOrWarning: JSX.Element;
  hasValidDestinationColumn: boolean;
  selectedDestinationColumnWithIndex: ColumnWithPositionalIndex | null; // null if no valid destination column
} {
  const [indexAmongValidColumns, setIndexAmongValidColumns] = useState(0);
  const validDestinationColumnsWithIndices = structuredTable.columns
    .map((column, index) => ({column, columnIndex: index}))
    .filter(
      ({column, columnIndex}) =>
        doesColumnHaveLinkedLocationSchema(column, structuredTable.schemas) &&
        columnIndex !== targetStorableColumnWithIndex.columnIndex
    );

  const selectedDestinationColumnWithIndex =
    validDestinationColumnsWithIndices[indexAmongValidColumns] ?? null;
  const destinationColumnSelector = (
    <div className={css(styles.columnSelector)}>
      <ColumnSelector
        label="Location column"
        onChange={setIndexAmongValidColumns}
        selectedColumnIndex={indexAmongValidColumns}
        columns={validDestinationColumnsWithIndices.map(({column}) => ({name: column.name}))}
        disableWhenOnlyOneColumn={false}
      />
    </div>
  );
  const noValidDestinationColumnWarning = (
    <div className={css(styles.informationalBanner)}>
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text={`${pluralize(
          numTasks,
          "This task doesn't",
          "These tasks don't"
        )} have a location column. Try searching for a location instead.`}
      />
    </div>
  );
  const hasValidDestinationColumn = selectedDestinationColumnWithIndex != null;
  const destinationColumnSelectorOrWarning = hasValidDestinationColumn
    ? destinationColumnSelector
    : noValidDestinationColumnWarning;
  return {destinationColumnSelectorOrWarning, selectedDestinationColumnWithIndex, hasValidDestinationColumn};
}

export function SelectedDestinationsField({
  selectedSampleIds,
  allParentIdsByStorableId,
}: {
  selectedSampleIds: Array<string>;
  allParentIdsByStorableId: Map<string, Array<string>>;
}): JSX.Element {
  const uniqueSelectedSampleIds = new Set(selectedSampleIds);
  const selectedDestinationIds = Array.from(allParentIdsByStorableId.entries())
    .filter(([targetStorableId, _]) => uniqueSelectedSampleIds.has(targetStorableId))
    .flatMap(([_, destinationIds]) => destinationIds);
  const uniqueSelectedDestinationIds = new Set(selectedDestinationIds);
  return (
    <>
      <FieldLabel label="Selected location" />
      <div className={css(styles.destinationStorableDiv)}>
        <span className={css(styles.destinationStorableSpan)}>
          {uniqueSelectedDestinationIds.size === 0 ? (
            <div className={css(styles.destinationStorableText)}>(No locations)</div>
          ) : uniqueSelectedDestinationIds.size === 1 ? (
            <StorableChip id={selectedDestinationIds[0]} size={ChipSize.SMALL} isLink={false} />
          ) : (
            <div className={css(styles.destinationStorableText)}>(Multiple locations)</div>
          )}
        </span>
      </div>
    </>
  );
}

export function MoveStorablesFromProcessTableModal<TTableMetadata, TRowMetadata>(
  props: ModalTypeProps<TTableMetadata, TRowMetadata>
): JSX.Element {
  const {structuredTable, targetStorableColumnWithIndex, targetRowsAndStorables} = props;
  const {destinationColumnSelectorOrWarning, selectedDestinationColumnWithIndex, hasValidDestinationColumn} =
    useSelectableDestinationColumn({
      structuredTable,
      targetStorableColumnWithIndex,
      numTasks: targetRowsAndStorables.length,
    });
  const tableKind = (structuredTable as UnknownStructuredTable).kind;
  assert(
    tableKind === StructuredTableKind.PROCESS_TASK || tableKind === StructuredTableKind.PROCESS_OUTPUT,
    'Expected either a task or an output process table'
  );
  const tableKindLabel =
    tableKind === StructuredTableKind.PROCESS_TASK
      ? ProcessTableKindLabel.TASK
      : ProcessTableKindLabel.OUTPUT;
  const processTaskOrOutputColumnWithIndex = getProcessTaskOrOutputColumnWithIndex(
    structuredTable.columns,
    tableKindLabel
  );

  const extractedRowDatas =
    selectedDestinationColumnWithIndex != null
      ? getExtractedRowDatas(
          targetRowsAndStorables,
          processTaskOrOutputColumnWithIndex,
          selectedDestinationColumnWithIndex
        )
      : []; // Can't extract missing destination data

  const initialValidationErrorDatas = getValidationErrorDatas(extractedRowDatas, tableKindLabel);
  const allInvalidTargetStorableIds = new Set(
    initialValidationErrorDatas.flatMap(({invalidTargetStorableIds}) => Array.from(invalidTargetStorableIds))
  );
  const allParentIdsByStorableId: Map<string, Array<string>> = new Map(
    extractedRowDatas.flatMap(({targetStorables, destinationStorables}) => {
      const parentIds = destinationStorables.map(({id}) => id);
      return targetStorables.map(({id}) => [id, parentIds]);
    })
  );

  const validParentIdByStorableId: Map<string, string> = new Map(
    Array.from(allParentIdsByStorableId.entries())
      .filter(([targetStorableId, _]) => !allInvalidTargetStorableIds.has(targetStorableId))
      .map(([targetStorableId, parentIds]) => {
        assert(parentIds.length === 1, 'valid tasks should only have 1 destination specified');
        return [targetStorableId, parentIds[0]];
      })
  );

  const getBlockingErrors = (selectedSampleIds: Array<string>): Array<string> => {
    const uniqueSelectedSampleIds = new Set(selectedSampleIds);
    const filteredExtractedRows = extractedRowDatas.filter(({targetStorables}) =>
      targetStorables.some(({id}) => selectedSampleIds.includes(id))
    );
    const validationErrorDatas = getValidationErrorDatas(filteredExtractedRows, tableKindLabel);
    const validationErrorDatasForSelectedIds = validationErrorDatas
      .map((data) => ({
        ...data,
        invalidTargetStorableIds: Array.from(data.invalidTargetStorableIds).filter((id) =>
          uniqueSelectedSampleIds.has(id)
        ),
      }))
      .filter(({invalidTargetStorableIds}) => invalidTargetStorableIds.length > 0);
    return validationErrorDatasForSelectedIds.map(({errorMessage}) => errorMessage);
  };

  const renderCustomInput = (selectedSampleIds: Array<string>): JSX.Element => (
    <>
      {destinationColumnSelectorOrWarning}
      {selectedDestinationColumnWithIndex != null && (
        <SelectedDestinationsField
          allParentIdsByStorableId={allParentIdsByStorableId}
          selectedSampleIds={selectedSampleIds}
        />
      )}
    </>
  );

  return (
    <MoveStorablesModal
      storables={props.storables}
      customParentStorableInputs={[
        {
          inputMode: 'structuredTableColumn',
          inputLabel: 'Look up locations from task',
          validParentIdByStorableId,
          renderCustomInput,
          getBlockingErrors,
          analyticsMetadataEntryType: ModalMetadataEntryType.AUTO_POPULATED_FROM_PROCESS_TASK,
        },
      ]}
      onRequestClose={props.onRequestClose}
      initialInputMode={hasValidDestinationColumn ? 'structuredTableColumn' : NATIVE_INPUT_MODE}
      analyticsMetadata={props.analyticsMetadata}
      shouldAlwaysShowSampleStatusTable={props.shouldAlwaysShowSampleStatusTable}
    />
  );
}

export default function QueryingMoveStorablesFromProcessTableModal<TTableMetadata, TRowMetadata>(
  props: Props<TTableMetadata, TRowMetadata>
): JSX.Element | null {
  return <MoveStorablesFromStructuredTableModal {...props} modalType={MoveStorablesFromProcessTableModal} />;
}

const styles = StyleSheet.create({
  columnSelector: {
    fontSize: 13,
    marginBottom: 10,
  },
  destinationStorableDiv: {
    backgroundColor: GRAY_LIGHTER,
  },
  destinationStorableSpan: {
    color: GRAY_DARKER,
  },
  destinationStorableText: {
    padding: '8px 12px',
    fontSize: 13,
  },
  informationalBanner: {
    marginTop: 10,
  },
});
